import { Note, Folder, NoteHistory } from '../types';
import { dbService as localDbService } from './database';
import { firestoreService } from './firestore';
import { authService, AuthUser } from './auth';

class HybridDatabaseService {
  private currentUser: AuthUser | null = null;
  private isOnline = navigator.onLine;
  private syncInProgress = false;

  constructor() {
    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.syncWhenOnline();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    // Listen for auth changes
    authService.onAuthStateChanged((user) => {
      this.currentUser = user;
      if (user && this.isOnline) {
        this.syncWhenOnline();
      }
    });
  }

  // Notes
  async getAllNotes(): Promise<Note[]> {
    if (this.isOnline && this.currentUser && !this.currentUser.isAnonymous) {
      try {
        const cloudNotes = await firestoreService.getAllNotes(this.currentUser.uid);
        // Update local cache
        await this.updateLocalCache(cloudNotes, []);
        return cloudNotes;
      } catch (error) {
        console.error('Failed to get cloud notes, falling back to local:', error);
      }
    }
    
    return await localDbService.getAllNotes();
  }

  async getNote(id: string): Promise<Note | undefined> {
    if (this.isOnline && this.currentUser) {
      try {
        const cloudNote = await firestoreService.getNote(id);
        return cloudNote || undefined;
      } catch (error) {
        console.error('Failed to get cloud note, falling back to local:', error);
      }
    }
    
    return await localDbService.getNote(id);
  }

  async addNote(note: Note): Promise<void> {
    // Always save locally first
    await localDbService.addNote(note);
    
    // Try to save to cloud if online and authenticated (not anonymous)
    if (this.isOnline && this.currentUser && !this.currentUser.isAnonymous) {
      try {
        await firestoreService.addNote(note, this.currentUser.uid);
      } catch (error) {
        console.error('Failed to save note to cloud, will sync later:', error);
        // Mark for later sync
        await this.markForSync(note.id, 'create');
      }
    } else {
      // Mark for later sync when online and authenticated
      await this.markForSync(note.id, 'create');
    }
  }

  async updateNote(id: string, updates: Partial<Note>, skipHistory: boolean = false): Promise<void> {
    // Always update locally first
    await localDbService.updateNote(id, updates, skipHistory);
    
    // Try to update cloud if online and authenticated
    if (this.isOnline && this.currentUser) {
      try {
        await firestoreService.updateNote(id, updates);
      } catch (error) {
        console.error('Failed to update note in cloud, will sync later:', error);
        await this.markForSync(id, 'update');
      }
    } else {
      await this.markForSync(id, 'update');
    }
  }

  async deleteNote(id: string): Promise<void> {
    // Always delete locally first
    await localDbService.deleteNote(id);
    
    // Try to delete from cloud if online and authenticated
    if (this.isOnline && this.currentUser) {
      try {
        await firestoreService.deleteNote(id);
      } catch (error) {
        console.error('Failed to delete note from cloud, will sync later:', error);
        await this.markForSync(id, 'delete');
      }
    } else {
      await this.markForSync(id, 'delete');
    }
  }

  // Folders
  async getAllFolders(): Promise<Folder[]> {
    if (this.isOnline && this.currentUser && !this.currentUser.isAnonymous) {
      try {
        const cloudFolders = await firestoreService.getAllFolders(this.currentUser.uid);
        return cloudFolders;
      } catch (error) {
        console.error('Failed to get cloud folders, falling back to local:', error);
      }
    }
    
    return await localDbService.getAllFolders();
  }

  async addFolder(folder: Folder): Promise<void> {
    await localDbService.addFolder(folder);
    
    if (this.isOnline && this.currentUser) {
      try {
        await firestoreService.addFolder(folder, this.currentUser.uid);
      } catch (error) {
        console.error('Failed to save folder to cloud:', error);
      }
    }
  }

  async updateFolder(id: string, updates: Partial<Folder>): Promise<void> {
    await localDbService.updateFolder(id, updates);
    
    if (this.isOnline && this.currentUser) {
      try {
        await firestoreService.updateFolder(id, updates);
      } catch (error) {
        console.error('Failed to update folder in cloud:', error);
      }
    }
  }

  async deleteFolder(id: string): Promise<void> {
    await localDbService.deleteFolder(id);
    
    if (this.isOnline && this.currentUser) {
      try {
        await firestoreService.deleteFolder(id);
      } catch (error) {
        console.error('Failed to delete folder from cloud:', error);
      }
    }
  }

  // History (local only for now)
  async getNoteHistory(noteId: string): Promise<NoteHistory[]> {
    return await localDbService.getNoteHistory(noteId);
  }

  // Sync operations
  private async markForSync(id: string, operation: 'create' | 'update' | 'delete'): Promise<void> {
    // Store sync queue in localStorage for simplicity
    const syncQueue = JSON.parse(localStorage.getItem('syncQueue') || '[]');
    syncQueue.push({ id, operation, timestamp: Date.now() });
    localStorage.setItem('syncQueue', JSON.stringify(syncQueue));
  }

  private async syncWhenOnline(): Promise<void> {
    if (this.syncInProgress || !this.isOnline || !this.currentUser) return;
    
    this.syncInProgress = true;
    
    try {
      // Process sync queue
      const syncQueue = JSON.parse(localStorage.getItem('syncQueue') || '[]');
      
      for (const item of syncQueue) {
        try {
          const localNote = await localDbService.getNote(item.id);
          
          switch (item.operation) {
            case 'create':
              if (localNote) {
                await firestoreService.addNote(localNote, this.currentUser.uid);
              }
              break;
            case 'update':
              if (localNote) {
                await firestoreService.updateNote(item.id, localNote);
              }
              break;
            case 'delete':
              await firestoreService.deleteNote(item.id);
              break;
          }
        } catch (error) {
          console.error(`Failed to sync ${item.operation} for ${item.id}:`, error);
          continue; // Continue with other items
        }
      }
      
      // Clear sync queue on success
      localStorage.removeItem('syncQueue');
      
      // Full sync: get latest from cloud
      const [cloudNotes, cloudFolders] = await Promise.all([
        firestoreService.getAllNotes(this.currentUser.uid),
        firestoreService.getAllFolders(this.currentUser.uid)
      ]);
      
      await this.updateLocalCache(cloudNotes, cloudFolders);
      
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      this.syncInProgress = false;
    }
  }

  private async updateLocalCache(notes: Note[], folders: Folder[]): Promise<void> {
    // This is a simple implementation - in production you'd want more sophisticated conflict resolution
    try {
      // Clear and rebuild local cache
      const localNotes = await localDbService.getAllNotes();
      const localFolders = await localDbService.getAllFolders();
      
      // For now, cloud wins in conflicts (last-write-wins)
      // In production, you'd implement proper conflict resolution
      
      // Update notes
      for (const cloudNote of notes) {
        const localNote = localNotes.find(n => n.id === cloudNote.id);
        if (!localNote || new Date(cloudNote.updatedAt) > new Date(localNote.updatedAt)) {
          await localDbService.updateNote(cloudNote.id, cloudNote, true);
        }
      }
      
      // Update folders
      for (const cloudFolder of folders) {
        const localFolder = localFolders.find(f => f.id === cloudFolder.id);
        if (!localFolder || new Date(cloudFolder.updatedAt) > new Date(localFolder.updatedAt)) {
          await localDbService.updateFolder(cloudFolder.id, cloudFolder);
        }
      }
      
    } catch (error) {
      console.error('Failed to update local cache:', error);
    }
  }

  // Export/Import for backup
  async exportData() {
    return await localDbService.exportData();
  }

  async importData(data: any) {
    return await localDbService.importData(data);
  }

  // Search (local for performance)
  async searchNotes(query: string): Promise<Note[]> {
    return await localDbService.searchNotes(query);
  }

  // Real-time subscriptions (when online and authenticated)
  subscribeToNotes(callback: (notes: Note[]) => void): (() => void) | null {
    if (this.isOnline && this.currentUser) {
      return firestoreService.subscribeToNotes(this.currentUser.uid, callback);
    }
    return null;
  }

  subscribeToFolders(callback: (folders: Folder[]) => void): (() => void) | null {
    if (this.isOnline && this.currentUser) {
      return firestoreService.subscribeToFolders(this.currentUser.uid, callback);
    }
    return null;
  }
}

export const hybridDbService = new HybridDatabaseService();